import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CustomerBranchService } from 'src/app/_services/dispo/customer-branch.service';
import { OrderService } from 'src/app/_services/dispo/order.service';

export interface DispatchBranchOption {
  id: number;
  label: string;
  short?: string;
  title?: string;
  email?: string;
  consultantEmail?: string;
  managerEmail?: string;
}

export interface DispatchTeamBaseOption {
  id: number;
  label: string;
  type?: string;
  isStammteam?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class DispatchPlannerTeamService {
  constructor(
    private customerBranchService: CustomerBranchService,
    private orderService: OrderService
  ) {}

  loadPlannerReferenceData(): Observable<{
    branches: DispatchBranchOption[];
    teamBases: any[];
  }> {
    return forkJoin({
      branches: this.customerBranchService.getAll().pipe(
        map(data => this.mapBranches(data)),
        catchError(() => of([] as DispatchBranchOption[]))
      ),
      teamBases: this.orderService.getFilter().pipe(
        map(data => (Array.isArray(data?.teamBases) ? data.teamBases : [])),
        catchError(() => of([]))
      )
    });
  }

  resolveBranchId(filialeCode: string, branches: DispatchBranchOption[]): number | null {
    const raw = (filialeCode || '').trim();
    if (!raw || !branches.length) {
      return null;
    }

    if (this.looksLikeEmail(raw)) {
      const byEmail = this.resolveBranchIdByEmail(raw, branches);
      if (byEmail != null) {
        return byEmail;
      }
    }

    const code = this.normalizeFilialeCode(raw);
    if (!code) {
      return null;
    }

    const byShort = branches.find(branch => this.normalizeFilialeCode(branch.short) === code);
    if (byShort) {
      return byShort.id;
    }

    if (/^\d{2}$/.test(code)) {
      const byTitleStartsWithShort = branches.find(branch => {
        const title = this.normalizeFilialeCode(branch.title);
        const short = this.normalizeFilialeCode(branch.short);
        return short === code || title.startsWith(`${code} `);
      });
      if (byTitleStartsWithShort) {
        return byTitleStartsWithShort.id;
      }
    }

    const byTitle = branches.find(branch => this.normalizeFilialeCode(branch.title) === code);
    if (byTitle) {
      return byTitle.id;
    }

    const byLabel = branches.find(branch => this.normalizeFilialeCode(branch.label) === code);
    if (byLabel) {
      return byLabel.id;
    }

    const byTitlePrefix = branches.find(branch => {
      const title = this.normalizeFilialeCode(branch.title);
      const short = this.normalizeFilialeCode(branch.short);
      return (
        (short && (code === short || code.startsWith(`${short} `) || code.startsWith(`${short} –`))) ||
        (title && (code === title || title.startsWith(`${code} `) || code.startsWith(`${title.split(' ')[0]} `)))
      );
    });
    if (byTitlePrefix) {
      return byTitlePrefix.id;
    }

    const dashParts = code.split(' – ').map(part => part.trim()).filter(Boolean);
    if (dashParts.length >= 2) {
      const byShortTitle = branches.find(branch => {
        const short = this.normalizeFilialeCode(branch.short);
        const title = this.normalizeFilialeCode(branch.title);
        return short === dashParts[0] && title === dashParts.slice(1).join(' – ');
      });
      if (byShortTitle) {
        return byShortTitle.id;
      }
    }

    const byLabelPrefix = branches.find(branch => {
      const label = this.normalizeFilialeCode(branch.label);
      return (
        label.startsWith(`${code} `) ||
        label.startsWith(`${code} –`) ||
        label.startsWith(`${code}-`)
      );
    });
    if (byLabelPrefix) {
      return byLabelPrefix.id;
    }

    if (/^\d+$/.test(code)) {
      const asNum = parseInt(code, 10);
      const byId = branches.find(branch => branch.id === asNum);
      if (byId) {
        return byId.id;
      }
    }

    const byPartialTitle = branches.find(branch => {
      const title = this.normalizeFilialeCode(branch.title || branch.label);
      return title.includes(code) || code.includes(title);
    });
    if (byPartialTitle) {
      return byPartialTitle.id;
    }

    const byPartialLabel = branches.find(branch => {
      const label = this.normalizeFilialeCode(branch.label);
      return label.includes(code) || code.includes(label);
    });
    if (byPartialLabel) {
      return byPartialLabel.id;
    }

    return null;
  }

  resolveBranchIdByEmail(email: string, branches: DispatchBranchOption[]): number | null {
    const normalized = this.normalizeEmail(email.replace(/^mailto:/i, ''));
    if (!normalized || !branches.length) {
      return null;
    }

    const exact = branches.find(
      branch =>
        this.normalizeEmail(branch.email) === normalized ||
        this.normalizeEmail(branch.consultantEmail) === normalized ||
        this.normalizeEmail(branch.managerEmail) === normalized
    );
    if (exact) {
      return exact.id;
    }

    const localPart = normalized.split('@')[0];
    if (localPart) {
      const byLocalPart = branches.find(branch => {
        const candidates = [branch.email, branch.consultantEmail, branch.managerEmail]
          .map(value => this.normalizeEmail(value))
          .filter(Boolean);
        return candidates.some(candidate => candidate!.split('@')[0] === localPart);
      });
      if (byLocalPart) {
        return byLocalPart.id;
      }
    }

    const byPartialEmail = branches.find(branch => {
      const candidates = [branch.email, branch.consultantEmail, branch.managerEmail]
        .map(value => this.normalizeEmail(value))
        .filter(Boolean);
      return candidates.some(
        candidate => candidate!.includes(normalized) || normalized.includes(candidate!)
      );
    });
    if (byPartialEmail) {
      return byPartialEmail.id;
    }

    return null;
  }

  private looksLikeEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  }

  private normalizeEmail(value: string | undefined | null): string {
    return (value || '').trim().toLowerCase();
  }

  private normalizeFilialeCode(value: string | undefined | null): string {
    return (value || '')
      .trim()
      .toLowerCase()
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .replace(/\s*[-–—]\s*/g, ' – ');
  }

  getTeamsForBranch(branchId: number, teamBases: any[]): DispatchTeamBaseOption[] {
    if (!branchId || !teamBases?.length) {
      return [];
    }

    return teamBases
      .filter(tb => this.teamServesBranch(tb, branchId))
      .map(tb => ({
        id: Number(tb.id),
        label: (tb.label || tb.name || `Team ${tb.id}`).trim(),
        type: tb.type,
        isStammteam: this.isStammTeam(tb, branchId)
      }))
      .filter(tb => Number.isFinite(tb.id))
      .sort((a, b) => a.label.localeCompare(b.label, 'de'));
  }

  resolveTeamBaseIdForTourIdentifier(
    tourIdentifier: string,
    branchId: number | null,
    teamBases: any[]
  ): number | null {
    const normalizedTour = this.normalizeTourIdentifier(tourIdentifier);
    if (!normalizedTour || branchId == null || !teamBases?.length) {
      return null;
    }

    for (const teamBase of teamBases) {
      if (!this.teamServesBranch(teamBase, branchId)) {
        continue;
      }

      const configuredTour = this.getCustomerTourIdentifier(teamBase, branchId);
      if (configuredTour && this.tourIdentifiersMatch(normalizedTour, configuredTour)) {
        const teamBaseId = Number(teamBase.id);
        return Number.isFinite(teamBaseId) ? teamBaseId : null;
      }
    }

    return null;
  }

  private normalizeTourIdentifier(value: string | undefined | null): string {
    return (value || '').trim().toUpperCase();
  }

  private tourIdentifiersMatch(left: string, right: string): boolean {
    const a = this.normalizeTourIdentifier(left);
    const b = this.normalizeTourIdentifier(right);
    if (!a || !b) {
      return false;
    }
    if (a === b) {
      return true;
    }

    const numericA = a.replace(/^0+/, '') || '0';
    const numericB = b.replace(/^0+/, '') || '0';
    return /^\d+$/.test(a) && /^\d+$/.test(b) && numericA === numericB;
  }

  private getCustomerTourIdentifier(teamBase: any, branchId: number): string | null {
    const raw =
      teamBase?.team_location_customer_tour_identifiers ??
      teamBase?.teamLocationCustomerTourIdentifiers;
    if (raw == null || raw === '') {
      return null;
    }

    let map: Record<string, unknown> = {};
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        map = parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
      } catch {
        return null;
      }
    } else if (typeof raw === 'object') {
      map = raw as Record<string, unknown>;
    } else {
      return null;
    }

    const key = String(branchId);
    const val = map[key] ?? map[branchId as unknown as string];
    if (val == null) {
      return null;
    }

    const configured = String(val).trim();
    return configured || null;
  }

  getBranchLabel(branchId: number | null, branches: DispatchBranchOption[]): string {
    if (branchId == null) {
      return '';
    }
    return branches.find(b => b.id === branchId)?.label || `Filiale ${branchId}`;
  }

  private mapBranches(data: unknown): DispatchBranchOption[] {
    const list = this.normalizeBranchPayload(data);
    return list
      .map(b => {
        const id = Number(b.id);
        if (!Number.isFinite(id)) {
          return null;
        }
        const title = String(b.title ?? '').trim();
        const short = String(b.short ?? '').trim();
        const label = title || short || `Filiale ${id}`;
        return {
          id,
          label: short && title ? `${short} – ${title}` : label,
          short,
          title,
          email: String(b.email ?? '').trim() || undefined,
          consultantEmail: String(b.consultant_email ?? '').trim() || undefined,
          managerEmail: String(b.manager_email ?? '').trim() || undefined
        } as DispatchBranchOption;
      })
      .filter((b): b is DispatchBranchOption => b != null)
      .sort((a, b) => a.label.localeCompare(b.label, 'de'));
  }

  private normalizeBranchPayload(data: unknown): Record<string, unknown>[] {
    if (Array.isArray(data)) {
      return data.filter(item => item && typeof item === 'object') as Record<string, unknown>[];
    }
    if (!data || typeof data !== 'object') {
      return [];
    }

    const payload = data as Record<string, unknown>;
    if (payload.id == null && typeof payload.status === 'string') {
      return [];
    }

    return Object.values(payload).filter(
      item =>
        !!item &&
        typeof item === 'object' &&
        (item as Record<string, unknown>).id != null
    ) as Record<string, unknown>[];
  }

  private teamServesBranch(teamBase: any, branchId: number): boolean {
    if (Number(teamBase?.customer_branch_id) === branchId) {
      return true;
    }
    const locations = this.normalizeIdArray(teamBase?.team_locations);
    return locations.includes(branchId);
  }

  private isStammTeam(teamBase: any, branchId: number): boolean {
    if (Number(teamBase?.customer_branch_id) === branchId) {
      return true;
    }
    const roles = this.parseJsonRecord(teamBase?.team_location_roles);
    return roles[String(branchId)] === 'stamm';
  }

  private normalizeIdArray(value: unknown): number[] {
    const raw = typeof value === 'string' ? this.parseJsonArray(value) : value;
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw.map(v => Number(v)).filter(n => Number.isFinite(n));
  }

  private parseJsonArray(value: string): unknown[] {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private parseJsonRecord(value: unknown): Record<string, string> {
    if (!value) {
      return {};
    }
    if (typeof value === 'object' && !Array.isArray(value)) {
      return value as Record<string, string>;
    }
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return typeof parsed === 'object' && parsed != null ? parsed : {};
      } catch {
        return {};
      }
    }
    return {};
  }
}
